// /var/www/devit-dashboard/src/pages/JobOffers.js
import { useState, useEffect } from 'react';
import { jobOffers as api } from '../services/api';

const MODALIDAD_COLOR = {
  presencial: { bg: '#1a2a4a', text: '#60b4ff' },
  remoto:     { bg: '#1a3a2a', text: '#00d4a0' },
  hibrido:    { bg: '#2a2a4a', text: '#a78bfa' },
};

const MODALIDADES = ['presencial', 'remoto', 'hibrido'];

const emptyForm = {
  titulo: '', descripcion: '', requisitos: '',
  modalidad: 'presencial', ubicacion: '', salario_info: '', activa: true,
};

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function JobOffers() {
  const [lista,     setLista]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [mostrarInactivas, setMostrarInactivas] = useState(false);
  const [modal,     setModal]     = useState(false);   // true = nuevo, obj = editar
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);
  const [ofertaLink, setOfertaLink] = useState(null);  // modal de link WA

  const cargar = (inactivas = mostrarInactivas) => {
    setLoading(true);
    api.getAll(!inactivas)
      .then(d => setLista(Array.isArray(d) ? d : []))
      .catch(() => setLista([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const abrirNuevo = () => { setForm(emptyForm); setModal(true); };
  const abrirEditar = (o) => {
    setForm({
      titulo: o.titulo, descripcion: o.descripcion || '',
      requisitos: o.requisitos || '', modalidad: o.modalidad || 'presencial',
      ubicacion: o.ubicacion || '', salario_info: o.salario_info || '', activa: o.activa,
    });
    setModal(o);
  };
  const cerrarModal = () => { setModal(false); setForm(emptyForm); };

  const handleGuardar = async () => {
    if (!form.titulo.trim()) return;
    setSaving(true);
    try {
      if (modal === true) {
        await api.create(form);
      } else {
        await api.update(modal.id, form);
      }
      cerrarModal();
      cargar();
    } finally {
      setSaving(false);
    }
  };

  const handleDesactivar = async (id) => {
    if (!window.confirm('¿Desactivar esta oferta?')) return;
    await api.deactivate(id);
    cargar();
  };

  const handleToggleInactivas = () => {
    const nuevo = !mostrarInactivas;
    setMostrarInactivas(nuevo);
    cargar(nuevo);
  };

  // Generar link wa.me con el ID de la oferta pre-rellenado
  const getWaLink = (oferta) => {
    const numero = ''; // el tenant pone su número en este campo en el futuro
    const texto  = encodeURIComponent(`Hola, quiero aplicar a la oferta ${oferta.id}`);
    return `https://wa.me/${numero}?text=${texto}`;
  };

  return (
    <div className="page" style={{ padding: '32px', maxWidth: '900px' }}>

      {/* ── Cabecera ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Ofertas de Trabajo</h1>
          <p style={{ color: 'var(--text2)', marginTop: '6px', fontSize: '14px' }}>
            Crea y gestiona tus vacantes. El agente usará los requisitos para filtrar candidatos.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleToggleInactivas}
            style={{
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
              border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)',
            }}
          >
            {mostrarInactivas ? 'Ocultar inactivas' : 'Ver inactivas'}
          </button>
          <button
            onClick={abrirNuevo}
            style={{
              padding: '9px 18px', borderRadius: '8px', fontSize: '13px',
              fontWeight: 700, cursor: 'pointer', border: 'none',
              background: 'var(--purple)', color: '#fff',
            }}
          >
            + Nueva oferta
          </button>
        </div>
      </div>

      {/* ── Lista de ofertas ─────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: 'var(--text2)' }}>Cargando ofertas...</p>
      ) : lista.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px',
          padding: '48px', textAlign: 'center', color: 'var(--text2)', fontSize: '14px'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
          <p style={{ margin: '0 0 16px' }}>No tienes ofertas activas todavía.</p>
          <button onClick={abrirNuevo} style={{
            padding: '9px 20px', borderRadius: '8px', background: 'var(--purple)',
            color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '13px'
          }}>
            Crear primera oferta
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {lista.map(o => {
            const mc = MODALIDAD_COLOR[o.modalidad] || MODALIDAD_COLOR.presencial;
            return (
              <div key={o.id} style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: '12px', padding: '20px 24px',
                opacity: o.activa ? 1 : 0.5,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    {/* Título + badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <span style={{ fontSize: '16px', fontWeight: 700 }}>{o.titulo}</span>
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                        fontWeight: 600, background: mc.bg, color: mc.text,
                      }}>
                        {o.modalidad}
                      </span>
                      {!o.activa && (
                        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', background: '#2a2a2a', color: '#888' }}>
                          inactiva
                        </span>
                      )}
                    </div>

                    {/* Detalles */}
                    <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--text2)', flexWrap: 'wrap' }}>
                      {o.ubicacion    && <span>📍 {o.ubicacion}</span>}
                      {o.salario_info && <span>💶 {o.salario_info}</span>}
                      <span>📅 {formatFecha(o.created_at)}</span>
                    </div>

                    {/* Requisitos preview */}
                    {o.requisitos && (
                      <p style={{
                        margin: '10px 0 0', fontSize: '12px', color: 'var(--text2)',
                        whiteSpace: 'pre-line', lineHeight: '1.6',
                        display: '-webkit-box', WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {o.requisitos}
                      </p>
                    )}
                  </div>

                  {/* Acciones */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '120px' }}>
                    <button onClick={() => abrirEditar(o)} style={btnStyle('var(--bg3)')}>✎ Editar</button>
                    <button onClick={() => setOfertaLink(o)} style={btnStyle('#1a2a1a', '#00d4a0')}>🔗 Link WA</button>
                    {o.activa && (
                      <button onClick={() => handleDesactivar(o.id)} style={btnStyle('#2a1a1a', '#ff6b6b')}>✕ Desactivar</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal: crear / editar oferta ────────────────────────── */}
      {modal !== false && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>
                {modal === true ? 'Nueva oferta' : 'Editar oferta'}
              </h2>
              <button onClick={cerrarModal} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: '20px' }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <label style={labelStyle}>
                Título del puesto *
                <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                  placeholder="ej: Desarrollador Backend Python" style={inputStyle} />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <label style={labelStyle}>
                  Modalidad
                  <select value={form.modalidad} onChange={e => setForm(f => ({ ...f, modalidad: e.target.value }))} style={inputStyle}>
                    {MODALIDADES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  Ubicación
                  <input value={form.ubicacion} onChange={e => setForm(f => ({ ...f, ubicacion: e.target.value }))}
                    placeholder="ej: Madrid" style={inputStyle} />
                </label>
              </div>

              <label style={labelStyle}>
                Salario (informativo)
                <input value={form.salario_info} onChange={e => setForm(f => ({ ...f, salario_info: e.target.value }))}
                  placeholder="ej: 30.000–40.000€" style={inputStyle} />
              </label>

              <label style={labelStyle}>
                Descripción pública
                <textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Descripción visible para el candidato..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </label>

              <label style={labelStyle}>
                Requisitos (el agente los usa para filtrar) *
                <textarea value={form.requisitos} onChange={e => setForm(f => ({ ...f, requisitos: e.target.value }))}
                  placeholder={"- Mínimo 2 años de experiencia\n- Conocimiento de X\n- Inglés B2"} rows={5}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }} />
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={cerrarModal} style={btnStyle('var(--bg3)')}>Cancelar</button>
              <button onClick={handleGuardar} disabled={saving || !form.titulo.trim()} style={{
                padding: '9px 20px', borderRadius: '8px', border: 'none',
                background: saving ? 'var(--bg3)' : 'var(--purple)', color: '#fff',
                fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px',
              }}>
                {saving ? 'Guardando...' : 'Guardar oferta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: link de WhatsApp ──────────────────────────────── */}
      {ofertaLink && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>🔗 Link de WhatsApp</h2>
              <button onClick={() => setOfertaLink(null)} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: '20px' }}>×</button>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text2)', marginBottom: '16px', lineHeight: '1.6' }}>
              Incluye el <strong>ID de la oferta</strong> en el texto del link para que el agente identifique automáticamente a qué puesto aplica el candidato.
            </p>

            <div style={{ background: 'var(--bg3)', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
              <p style={{ fontSize: '11px', color: 'var(--text2)', margin: '0 0 6px', fontWeight: 600, textTransform: 'uppercase' }}>Texto pre-rellenado para el candidato:</p>
              <code style={{ fontSize: '12px', color: '#a78bfa', wordBreak: 'break-all', lineHeight: '1.6' }}>
                Hola, quiero aplicar a la oferta {ofertaLink.id}
              </code>
            </div>

            <div style={{ background: 'var(--bg3)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <p style={{ fontSize: '11px', color: 'var(--text2)', margin: '0 0 6px', fontWeight: 600, textTransform: 'uppercase' }}>Formato del link (sustituye TU_NUMERO):</p>
              <code style={{ fontSize: '11px', color: '#60b4ff', wordBreak: 'break-all', lineHeight: '1.6' }}>
                {`https://wa.me/34XXXXXXXXX?text=Hola%2C+quiero+aplicar+a+la+oferta+${ofertaLink.id}`}
              </code>
            </div>

            <p style={{ fontSize: '12px', color: 'var(--text2)', lineHeight: '1.6' }}>
              Pega este link en tu web, LinkedIn o anuncio. Cuando el candidato lo abra en WhatsApp, el texto ya vendrá pre-rellenado con el ID de la oferta.
            </p>

            <button
              onClick={() => {
                navigator.clipboard.writeText(`https://wa.me/34XXXXXXXXX?text=Hola%2C+quiero+aplicar+a+la+oferta+${ofertaLink.id}`);
              }}
              style={{ ...btnStyle('var(--purple)', '#fff'), marginTop: '12px', width: '100%', padding: '10px' }}
            >
              📋 Copiar link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Estilos ───────────────────────────────────────────────────────────────
const btnStyle = (bg, color = 'var(--text2)') => ({
  padding: '7px 12px', borderRadius: '7px', border: '1px solid var(--border)',
  background: bg, color, fontSize: '12px', cursor: 'pointer', fontWeight: 600,
  whiteSpace: 'nowrap',
});

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
};

const modalStyle = {
  background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '16px',
  padding: '28px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto',
};

const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: '6px',
  fontSize: '13px', fontWeight: 600, color: 'var(--text1)',
};

const inputStyle = {
  padding: '9px 12px', borderRadius: '8px', border: '1px solid var(--border)',
  background: 'var(--bg3)', color: 'var(--text1)', fontSize: '13px', outline: 'none',
};
